import { AuthResponse, User } from './IAuth';
import { ApiLocalStorage } from '../utils/ApiLocalStorage';
import { apiUserAccount } from '../services/api/ApiService';
import { ApiEndpoints } from '../modules/account/constantes/ApiEndpoints';
import { SCHEMA_STORAGE_KEY } from 'modules/account/services/SchemaService';
import { USER } from '../modules/account/constantes/LocalStorageEnum';
import { generateDeviceData } from '../modules/account/services/AccountService';
import { getSchemaIdFromStorage } from '../utils/getSchemaIdFromStorage';
import { env } from '../config/env';

class UserManager {
  events = {
    userLoadedCallbacks: [] as Array<(user: User) => void>,
    userUnloadedCallbacks: [] as Array<() => void>,
    
    addUserLoaded(callback: (user: User) => void): void {
      this.userLoadedCallbacks.push(callback);
    },
    
    addUserUnloaded(callback: () => void): void {
      this.userUnloadedCallbacks.push(callback);
    },
    
    removeUserLoaded(callback: (user: User) => void): void {
      this.userLoadedCallbacks = this.userLoadedCallbacks.filter(cb => cb !== callback);
    },
    
    removeUserUnloaded(callback: () => void): void {
      this.userUnloadedCallbacks = this.userUnloadedCallbacks.filter(cb => cb !== callback);
    },
    
    triggerUserLoaded(user: User): void {
      this.userLoadedCallbacks.forEach(callback => callback(user));
    },
    
    triggerUserUnloaded(): void {
      this.userUnloadedCallbacks.forEach(callback => callback());
    }
  };
  private user: User | null = null;
  
  private saveCampaignIdToLocalStorage(): void {
    if (env.CAMPAIGN_ID) {
      ApiLocalStorage.set('campaign-id', env.CAMPAIGN_ID);
    }
  }
  async setUser(username: string, accessToken: string, expiresIn: string) {
    try {
      const schemaId = getSchemaIdFromStorage();
      if (!schemaId) {
        throw new Error('Schema da conta não carregado. Não é possível obter os dados do usuário.');
      }

      const user = await apiUserAccount.get<AuthResponse>(`${ApiEndpoints.account.getUserMeData}?schemaId=${schemaId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }) as any;
      
      this.user = {
        profile: {
          name: user.fullname,
          ...user
        },
        access_token: accessToken,
        id_token: accessToken,
        expires_in: expiresIn,
        status: 'active',
        updated_at: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('Erro ao obter dados do usuário no setUser:', error);
      throw error;
    }
  }
  
  async getParticipantId(username: string): Promise<string | null> {
    try {
      const rawParticipantId = localStorage.getItem('@pwa-participantId');
      const participantIdFromApiStorage = ApiLocalStorage.get('@pwa-participantId');
      
      let participantId = rawParticipantId || participantIdFromApiStorage;
      
      if (participantId && participantId.trim() !== '') {
        return participantId;
      }

      const schemaId = ApiLocalStorage.get(SCHEMA_STORAGE_KEY)?.id;
      if (!schemaId) {
        return null;
      }
      
      const response = await apiUserAccount.get(`${ApiEndpoints.auth.userSearch}/${username}?schemaId=${schemaId}`) as any;
      
      if (response?.participantId) {
        
        localStorage.setItem('@pwa-participantId', response.participantId);
        
        return response.participantId;
      }
      
      return null;
      
    } catch (error) {
      return null;
    }
  }
  
  async login(username: string, password: string, token: string): Promise<User> {
    
    if (password.includes('accessToken')) {
      const decode = JSON.parse(password);
      
      return this.loginOtp(username, decode.accessToken, decode.expiresIn);
    }
    
    const deviceData = await generateDeviceData();
    
    let participantId = await this.getParticipantId(username);
    
    if (!participantId) {
      throw new Error('ParticipantId é obrigatório para realizar o login. Por favor, refaça o processo de busca de usuário.');
    }
    
    const data = await apiUserAccount.post<AuthResponse>(ApiEndpoints.auth.password, {
        username,
        password,
        participantId,
        deviceData
      },
      {
        headers: {
          'X-Challenge-Verify': token,
          'X-Challenge-Host': process.env.REACT_APP_TURNSTILE_HOST
        }
      }) as any;
    
    await this.setUser(username, data.accessToken, data.expiresIn);
    
    if (!this.user) {
      throw new Error('User not initialized');
    }
    
    ApiLocalStorage.set(USER, this.user);
    
    const expirationTime = new Date().getTime() + 55 * 60 * 1000;
    ApiLocalStorage.set('session-expiration', expirationTime);
    
    this.events.triggerUserLoaded(this.user);
    
    return this.user;
  }
  
  async loginOtp(username: string, accessToken: string, expiresIn: string): Promise<User> {
    
    try {
      const participantId = await this.getParticipantId(username);
      
      if (!participantId) {
        throw new Error('ParticipantId é obrigatório para realizar o login. Por favor, refaça o processo de busca de usuário.');
      }
      
      await this.setUser(
        username,
        accessToken,
        expiresIn
      );
      
      if (!this.user) {
        throw new Error('User not initialized');
      }
      
      // Adicionar participantId ao perfil do usuário
      this.user.profile.participantId = participantId;
      
      ApiLocalStorage.set(USER, this.user);
      
      const expirationTime = new Date().getTime() + 55 * 60 * 1000;
      ApiLocalStorage.set('session-expiration', expirationTime);
      
      this.events.triggerUserLoaded(this.user);
      
      return this.user;
      
    } catch (error) {
      throw error;
    }
  }
  
  async loginOtpWithUserData(username: string, accessToken: string, expiresIn: string, userData: any): Promise<User> {
    
    try {
      const participantId = await this.getParticipantId(username);
      
      if (!participantId) {
        throw new Error('ParticipantId é obrigatório para realizar o login. Por favor, refaça o processo de busca de usuário.');
      }
      
      this.user = {
        profile: {
          name: userData.fullname || userData.name || username,
          username,
          email: userData.email || '',
          fullname: userData.fullname || userData.name || username,
          phoneNumber: userData.phoneNumber || userData.phone || '',
          participantId: participantId,
          address_postalcode: userData.address_postalcode || '',
          address_street: userData.address_street || '',
          address_district: userData.address_district || '',
          address_number: userData.address_number || '',
          address_city: userData.address_city || '',
          address_state: userData.address_state || '',
          address_complement: userData.address_complement || '',
          address_reference: userData.address_reference || ''
        },
        access_token: accessToken,
        id_token: accessToken,
        expires_in: expiresIn,
        status: 'active',
        updated_at: new Date().toISOString()
      };
      
      ApiLocalStorage.set(USER, this.user);
      
      const expirationTime = new Date().getTime() + 55 * 60 * 1000;
      ApiLocalStorage.set('session-expiration', expirationTime);
      
      this.events.triggerUserLoaded(this.user);
      
      return this.user;
      
    } catch (error) {
      throw error;
    }
  }
  
  async loginWithCallback(accessToken: string, expiresIn: string): Promise<User> {
    try {
      await this.setUser('', accessToken, expiresIn);

      if (!this.user) {
        throw new Error('User not initialized');
      }

      ApiLocalStorage.set(USER, this.user);

      const expirationTime = new Date().getTime() + 55 * 60 * 1000;
      ApiLocalStorage.set('session-expiration', expirationTime);

      this.events.triggerUserLoaded(this.user);

      return this.user;

    } catch (error) {
      console.error('Erro no login via callback:', error);
      throw error;
    }
  }

  async getUser(): Promise<User | null> {
    try {
      if (this.user) {
        return this.user;
      }
      
      const storedUser = ApiLocalStorage.get(USER);
      
      if (storedUser) {
        this.user = storedUser;
        
        return this.user;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }
  
  async signoutRedirect(): Promise<void> {
    try {
      this.user = null;
      ApiLocalStorage.set(USER, null);
      this.events.triggerUserUnloaded();
    } catch (error) {
      throw error;
    }
  }
}

export default new UserManager();
